import { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Autocomplete,
  Chip,
} from '@mui/material';
import * as adminService from '../../services/adminService';
import * as adminOrgService from '../../services/adminOrganizationService';
import { adminChangeUserTier, adminChangeOrgTier } from '../../services/subscriptionService';
import { infoNotification, errorNotification } from '../../services/notificationService';

const TIERS = ['beta', 'bronze', 'silver', 'gold'];

const tierColors = {
  beta: 'info',
  bronze: 'warning',
  silver: 'default',
  gold: 'success',
};

function tierLabel(tier) {
  if (!tier) return '';
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}

export default function AdminSubscriptionsTab() {
  // User search
  const [selectedUser, setSelectedUser] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  const [userOptions, setUserOptions] = useState([]);
  const [userTier, setUserTier] = useState('bronze');
  const [userUpdating, setUserUpdating] = useState(false);

  // Org search
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgSearch, setOrgSearch] = useState('');
  const [orgOptions, setOrgOptions] = useState([]);
  const [orgTier, setOrgTier] = useState('bronze');
  const [orgUpdating, setOrgUpdating] = useState(false);

  // Debounced user search
  useEffect(() => {
    if (!userSearch) {
      setUserOptions([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const data = await adminService.listUsers(userSearch);
        setUserOptions(data.users || []);
      } catch {
        setUserOptions([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [userSearch]);

  // Debounced org search
  useEffect(() => {
    if (!orgSearch) {
      setOrgOptions([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const data = await adminOrgService.listOrganizations(orgSearch);
        setOrgOptions(data.organizations || []);
      } catch {
        setOrgOptions([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [orgSearch]);

  // Pre-fill tier dropdown when user is selected
  const handleUserSelected = (event, value) => {
    setSelectedUser(value);
    if (value?.subscriptionTier) {
      setUserTier(value.subscriptionTier);
    }
  };

  // Pre-fill tier dropdown when org is selected
  const handleOrgSelected = (event, value) => {
    setSelectedOrg(value);
    if (value?.subscriptionTier) {
      setOrgTier(value.subscriptionTier);
    }
  };

  const handleChangeUserTier = async () => {
    if (!selectedUser) {
      errorNotification('Select a user');
      return;
    }
    setUserUpdating(true);
    infoNotification(`Updating ${selectedUser.username} to ${tierLabel(userTier)}...`);
    try {
      await adminChangeUserTier(selectedUser.id, userTier);
      // Re-fetch user to confirm change
      const data = await adminService.listUsers(selectedUser.username);
      const updated = (data.users || []).find((u) => u.id === selectedUser.id);
      if (updated && updated.subscriptionTier === userTier) {
        infoNotification(`${updated.username} confirmed as ${tierLabel(updated.subscriptionTier)}`);
      } else {
        errorNotification('Tier update sent but could not verify — please check manually');
      }
      setSelectedUser(null);
      setUserSearch('');
    } catch (error) {
      errorNotification(error.response?.data?.error || 'Failed to update user tier');
    } finally {
      setUserUpdating(false);
    }
  };

  const handleChangeOrgTier = async () => {
    if (!selectedOrg) {
      errorNotification('Select an organization');
      return;
    }
    setOrgUpdating(true);
    infoNotification(`Updating ${selectedOrg.name} to ${tierLabel(orgTier)}...`);
    try {
      await adminChangeOrgTier(selectedOrg.id, orgTier);
      // Re-fetch org to confirm change
      const data = await adminOrgService.listOrganizations(selectedOrg.name);
      const updated = (data.organizations || []).find((o) => o.id === selectedOrg.id);
      if (updated && updated.subscriptionTier === orgTier) {
        infoNotification(`${updated.name} confirmed as ${tierLabel(updated.subscriptionTier)}`);
      } else {
        errorNotification('Tier update sent but could not verify — please check manually');
      }
      setSelectedOrg(null);
      setOrgSearch('');
    } catch (error) {
      errorNotification(error.response?.data?.error || 'Failed to update organization tier');
    } finally {
      setOrgUpdating(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Alert severity="info">
        Subscription tier changes take effect immediately. The user&apos;s storage quota
        will be updated to match their new tier.
      </Alert>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Change User Tier
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <Autocomplete
              options={userOptions}
              getOptionLabel={(option) => `${option.username} (${option.email})`}
              value={selectedUser}
              onChange={handleUserSelected}
              onInputChange={(e, value) => setUserSearch(value)}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                      {option.username} ({option.email})
                    </Typography>
                    {option.subscriptionTier && (
                      <Chip
                        label={tierLabel(option.subscriptionTier)}
                        color={tierColors[option.subscriptionTier] || 'default'}
                        size="small"
                      />
                    )}
                  </Stack>
                </li>
              )}
              renderInput={(params) => <TextField {...params} label="Search users..." size="small" />}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              noOptionsText={userSearch ? 'No users found' : 'Type to search users'}
              sx={{ minWidth: 350 }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Tier</InputLabel>
              <Select value={userTier} label="Tier" onChange={(e) => setUserTier(e.target.value)}>
                {TIERS.map((t) => (
                  <MenuItem key={t} value={t}>
                    {tierLabel(t)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="contained" onClick={handleChangeUserTier} disabled={!selectedUser || userUpdating}>
              {userUpdating ? 'Updating...' : 'Update'}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Change Organization Tier
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <Autocomplete
              options={orgOptions}
              getOptionLabel={(option) => option.name}
              value={selectedOrg}
              onChange={handleOrgSelected}
              onInputChange={(e, value) => setOrgSearch(value)}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                      {option.name}
                    </Typography>
                    {option.subscriptionTier && (
                      <Chip
                        label={tierLabel(option.subscriptionTier)}
                        color={tierColors[option.subscriptionTier] || 'default'}
                        size="small"
                      />
                    )}
                  </Stack>
                </li>
              )}
              renderInput={(params) => <TextField {...params} label="Search organizations..." size="small" />}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              noOptionsText={orgSearch ? 'No organizations found' : 'Type to search organizations'}
              sx={{ minWidth: 350 }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Tier</InputLabel>
              <Select value={orgTier} label="Tier" onChange={(e) => setOrgTier(e.target.value)}>
                {TIERS.map((t) => (
                  <MenuItem key={t} value={t}>
                    {tierLabel(t)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="contained" onClick={handleChangeOrgTier} disabled={!selectedOrg || orgUpdating}>
              {orgUpdating ? 'Updating...' : 'Update'}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}
